/**
 * CostRollupSection Component
 * AIDLC Administrative Backend
 *
 * Where the tokens went, beside what was estimated: for the software unit as
 * a whole, for each customer, for each project, and for each agent split by
 * the projects it worked on.
 *
 * Spend that names no project is shown on its own line and is never shared
 * out among customers. A project with no customer is the unit's own work.
 */

import { useState, useEffect, Fragment } from 'react';
import { getCostRollup } from '../../services/intakeService';
import type { CostRollup, Spend, Estimated } from '../../services/intakeService';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const count = new Intl.NumberFormat('en-US');

const money = (value: number | null): string => (value === null ? '—' : usd.format(value));
const estimatedCost = (e: Estimated): string => (e.items === 0 ? '—' : usd.format(e.costUsd));

/** Estimate less actual: under the estimate reads as good, over it as bad, and nothing estimated says so. */
function Variance({ value }: { value: number | null }) {
  if (value === null) return <span className="text-gray-400">No estimate</span>;
  const over = value < 0;
  return <span className={over ? 'text-red-600' : 'text-emerald-600'}>{over ? `${usd.format(-value)} over` : `${usd.format(value)} under`}</span>;
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums text-black dark:text-white">{value}</div>
      {note && <div className="text-[11px] text-gray-500 mt-1">{note}</div>}
    </div>
  );
}

function Panel({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
      <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
        <div className="text-xs font-semibold text-black dark:text-white">{title}</div>
        {note && <div className="text-[11px] text-gray-500 mt-0.5">{note}</div>}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

const TH = ({ children, right }: { children: React.ReactNode; right?: boolean }) => (
  <th scope="col" className={`${right ? 'text-right' : 'text-left'} px-4 py-2 font-medium`}>{children}</th>
);
const NUM = 'px-4 py-2 text-right tabular-nums';

function SpendCells({ actual }: { actual: Spend }) {
  return (
    <>
      <td className={NUM}>{count.format(actual.sessions)}</td>
      <td className={NUM}>{count.format(actual.totalTokens)}</td>
      <td className={`${NUM} font-medium`}>{usd.format(actual.costUsd)}</td>
    </>
  );
}

export function CostRollupSection() {
  const [rollup, setRollup] = useState<CostRollup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openAgent, setOpenAgent] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCostRollup()
      .then((data) => { if (!cancelled) setRollup(data); })
      .catch((err) => {
        console.error('[CostRollupSection] Failed to load the roll-up:', err);
        if (!cancelled) setError('The cost roll-up could not be loaded. Try again in a moment.');
      });
    return () => { cancelled = true; };
  }, []);

  if (error) return <div className="text-xs text-red-600" role="alert">{error}</div>;
  if (!rollup) return <div className="text-xs text-gray-500">Loading the cost roll-up…</div>;

  const { unit, customers, internal, unattributed, projects, agents } = rollup;
  const unitVariance = unit.estimated.items > 0 ? unit.estimated.costUsd - (unit.actual.costUsd - unattributed.costUsd) : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Figure label="Unit AI cost" value={usd.format(unit.actual.costUsd)} note={`${count.format(unit.actual.sessions)} agent sessions, every project and none`} />
        <Figure label="Estimated" value={estimatedCost(unit.estimated)} note={`${count.format(unit.estimated.items)} estimated backlog items`} />
        <Figure label="Customer revenue" value={money(unit.revenueUsd)} note={unit.revenueUsd === null ? 'The CRM has sent no figure' : `${count.format(unit.customers)} customers, from the CRM`} />
        <Figure label="Not attributed" value={usd.format(unattributed.costUsd)} note="Spend that names no project; shared out to nobody" />
      </div>
      {unitVariance !== null && (
        <div className="text-[11px] text-gray-500">Against the estimates, attributed spend is <Variance value={unitVariance} />. Estimates cover only items that carry one.</div>
      )}

      <Panel title="By customer" note="Projects the CRM opened. Margin is the CRM's revenue less the AI cost.">
        {customers.length === 0 ? (
          <div className="px-4 py-6 text-xs text-gray-500">No customer project has been opened from the CRM yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                <TH>Customer</TH><TH right>Projects</TH><TH right>Sessions</TH><TH right>Tokens</TH><TH right>AI cost</TH><TH right>Estimated</TH><TH right>Against estimate</TH><TH right>Revenue</TH><TH right>Margin</TH>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.crmCustomerId} className="border-t border-zinc-100 dark:border-zinc-800">
                  <td className="px-4 py-2 text-black dark:text-white">{c.customerName}</td>
                  <td className={NUM}>{count.format(c.projects)}</td>
                  <SpendCells actual={c.actual} />
                  <td className={NUM}>{estimatedCost(c.estimated)}</td>
                  <td className={NUM}><Variance value={c.varianceUsd} /></td>
                  <td className={NUM}>{money(c.revenueUsd)}</td>
                  <td className={NUM}>{money(c.marginUsd)}</td>
                </tr>
              ))}
              <tr className="border-t border-zinc-200 dark:border-zinc-700 text-gray-500">
                <td className="px-4 py-2">LDAJA's own projects</td>
                <td className={NUM}>{count.format(internal.projects)}</td>
                <SpendCells actual={internal.actual} />
                <td className={NUM}>{estimatedCost(internal.estimated)}</td>
                <td className={NUM}>—</td><td className={NUM}>—</td><td className={NUM}>—</td>
              </tr>
              <tr className="border-t border-zinc-100 dark:border-zinc-800 text-gray-500">
                <td className="px-4 py-2">Not attributed to a project</td>
                <td className={NUM}>—</td>
                <SpendCells actual={unattributed} />
                <td className={NUM}>—</td><td className={NUM}>—</td><td className={NUM}>—</td><td className={NUM}>—</td>
              </tr>
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="By project" note="Estimate is the sum of the estimates on the project's backlog items.">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-500">
              <TH>Project</TH><TH>Customer</TH><TH right>Open / done</TH><TH right>Sessions</TH><TH right>Tokens</TH><TH right>AI cost</TH><TH right>Estimated</TH><TH right>Against estimate</TH>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.projectId} className="border-t border-zinc-100 dark:border-zinc-800">
                <td className="px-4 py-2 text-black dark:text-white">{p.projectName}</td>
                <td className="px-4 py-2">{p.customerName ?? <span className="text-gray-400">LDAJA</span>}</td>
                <td className={NUM}>{count.format(p.openItems)} / {count.format(p.doneItems)}</td>
                <SpendCells actual={p.actual} />
                <td className={NUM}>{estimatedCost(p.estimated)}</td>
                <td className={NUM}><Variance value={p.varianceUsd} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="By agent, split by project" note="Open an agent to see which projects its spend went to.">
        {agents.length === 0 ? (
          <div className="px-4 py-6 text-xs text-gray-500">No agent work has been recorded yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                <TH>Agent</TH><TH right>Projects</TH><TH right>Sessions</TH><TH right>Tokens</TH><TH right>AI cost</TH>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => {
                const open = openAgent === a.agentId;
                return (
                  <Fragment key={a.agentId}>
                    <tr className="border-t border-zinc-100 dark:border-zinc-800">
                      <td className="px-4 py-2">
                        <button type="button" className="text-left font-medium text-black dark:text-white hover:underline" aria-expanded={open} onClick={() => setOpenAgent(open ? null : a.agentId)}>
                          <span aria-hidden="true">{open ? '▾' : '▸'}</span> {a.agentName}
                        </button>
                      </td>
                      <td className={NUM}>{count.format(a.byProject.filter((p) => p.projectId !== null).length)}</td>
                      <SpendCells actual={a} />
                    </tr>
                    {open && a.byProject.map((p) => (
                      <tr key={`${a.agentId}:${p.projectId ?? 'none'}`} className="bg-zinc-50 dark:bg-zinc-950 text-gray-600 dark:text-gray-400">
                        <td className="pl-10 pr-4 py-1.5" colSpan={2}>{p.projectName}</td>
                        <SpendCells actual={p} />
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
